/* eslint-disable react-hooks/exhaustive-deps */
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Autocomplete, Backdrop, ButtonGroup, Pagination } from "@mui/material";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Snackbar from "@mui/material/Snackbar";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { queryLimit, REPORT_TYPES } from "../../constants";

import usePageNavigation from "../../hooks/usePageNavigation";

import {
    createNewReport,
    getAllMyReports,
} from "../../services/requests/ApiRequests";
import { getQueryParams } from "../../utils";
import CashPicker from "./CashPicker";
import DatePickerRange from "./DatePicker";

function Reports(props) {
    let navigate = useNavigate();
    const { t } = useTranslation();
    const { pageNumber = "1" } = useParams();
    const [sortingDates, setSortingDates] = useState({
        dateFrom: null,
        dateTo: null,
    });
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openSuccess, setOpenSuccess] = useState(false);
    const [error, setError] = useState({
        status: false,
        msg: "Error",
    });
    const [selectedCashRegister, setSelectedCashRegister] = useState("");
    const [selectedReportType, setSlectedReportType] = useState("1"); // TODO: required? compare with ekassa
    const [selectedCashierId, setSelectedCashierId] = useState("");
    const [selectedCashierName, setSelectedCashierName] = useState("");
    const [reportBthVisibility, setReportBthVisibility] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(null);
    const [reportStart, setReportStart] = useState(0);
    const [card, setCard] = useState({});
    const [organization, setOrganization] = useState(null);

    // New states for report range selection
    const [fromReport, setFromReport] = useState(null);
    const [toReport, setToReport] = useState(null);

    const redirectLink = "acps/reports";

    usePageNavigation(
        setCurrentPage,
        setReportStart,
        pageNumber,
        queryLimit,
        pageCount,
        navigate,
        redirectLink
    );

    const isCashRegisterReadyToSubmit = !!organization;

    const onSubmitCashRegister = (data) => {
        setCard(data.formData);
        setOrganization(data.organization);
    };

    const handlePagination = (event, page) => {
        navigate(`/${redirectLink}/${page}`);
    };

    const getReports = () => {
        const params = {
            report_type: selectedReportType,
            limit: queryLimit,
            start: reportStart,
            date_from: sortingDates.dateFrom,
            date_to: sortingDates.dateTo,
            ...card,
            type: 3,
        };
        getAllMyReports(params).then((data) => {
            setReports(data.data.data);
            setPageCount(Math.ceil(data.data.count / queryLimit));
            setLoading(false);
        });
    };

    useEffect(() => {
        if (!organization) return;
        getReports();
    }, [
        JSON.stringify(sortingDates),
        selectedCashierId,
        selectedCashierName,
        selectedReportType,
        selectedCashRegister,
        currentPage,
        organization,
    ]);

    useEffect(() => {
        if (organization) {
            setReportBthVisibility(true);
        } else {
            setReportBthVisibility(false);
        }
    }, [organization]);

    function generateReport(type) {
        const reqBody = {
            card,
            type,
        };

        createNewReport(reqBody)
            .then((data) => {
                if (data.data?.data?.length) {
                    setLoading(true);
                    getReports();
                } else {
                    setError({
                        status: true,
                        msg: "There are errors while report generation",
                    });
                }
            })
            .catch((error) => {
                const data = error?.response?.data || {};
                const msg =
                    data.error || "There are errors while receipt generation";
                setError({
                    status: true,
                    msg,
                    code: error?.response?.status,
                });
            });
    }

    const handleChangeReportType = (event) => {
        const inputValue =
            event.target.value === "all" ? "" : event.target.value;
        setSlectedReportType(inputValue);
    };

    const handleClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }

        setOpenSuccess(false);
        setError({
            status: false,
            msg: "Error",
        });
    };

    const handleReportsNavigate = (type) => {
        if (!selectedCashRegister) return;

        let dateFrom, dateTo;

        if (fromReport && toReport) {
            const fromDateTime = fromReport.soap_data.datetime;
            const toDateTime = toReport.soap_data.datetime;

            if (fromDateTime > toDateTime) {
                dateFrom = toDateTime;
                dateTo = fromDateTime;
            } else {
                dateFrom = fromDateTime;
                dateTo = toDateTime;
            }
        } else {
            dateFrom = sortingDates.dateFrom;
            dateTo = sortingDates.dateTo;
        }

        const path =
            type === "periodic" ? "periodic-reports" : "gestionar-reports";
        navigate(
            `/acps/${path}?rid=${selectedCashRegister?._id}&date_from=${dateFrom}&date_to=${dateTo}`
        );
    };

    const getReportOptionLabel = (report) => {
        if (!report) return "";
        return `#${report.soap_data.report}`;
    };

    const getFromReportOptions = () => {
        if (!toReport) return reports;
        return reports.filter((report) => report._id <= toReport._id);
    };

    const getToReportOptions = () => {
        if (!fromReport) return reports;
        return reports.filter((report) => report._id >= fromReport._id);
    };

    const handleFromReportChange = (event, newValue) => {
        setFromReport(newValue);
        if (toReport && newValue && toReport._id < newValue._id) {
            setToReport(null);
        }
    };

    const handleToReportChange = (event, newValue) => {
        setToReport(newValue);
        if (fromReport && newValue && fromReport._id > newValue._id) {
            setFromReport(null);
        }
    };

    const isSfsError = error.code === 450;
    const isDatesSelected = sortingDates.dateFrom && sortingDates.dateTo;
    const isReportRangeSelected = fromReport && toReport;

    const canNavigateToReports =
        (isDatesSelected || isReportRangeSelected) &&
        selectedReportType === "1" &&
        reports.length > 0;

    return (
        <>
            {error.status && isSfsError ? (
                <Backdrop open={error.status} sx={{ zIndex: 99999 }}>
                    <Box
                        style={{
                            display: "flex",
                            width: 500,
                            alignItems: "center",
                            flexDirection: "column",
                            justifyContent: "center",
                            backgroundColor: "#fff",
                            padding: 16,
                            borderRadius: 5,
                        }}
                    >
                        <WarningAmberIcon color="error" sx={{ fontSize: 40 }} />
                        <p>{`${error.msg}`}</p>
                        <Button
                            onClick={() => setError({ status: false, msg: "" })}
                        >
                            {t("retry")}
                        </Button>
                    </Box>
                </Backdrop>
            ) : error.status ? (
                <Snackbar
                    open={error.status}
                    autoHideDuration={6000}
                    onClose={handleClose}
                >
                    <Alert
                        onClose={handleClose}
                        severity="error"
                        sx={{ width: "100%" }}
                    >
                        {error.msg}
                    </Alert>
                </Snackbar>
            ) : null}
            <Box
                sx={{
                    flexGrow: 1,
                    marginTop: 3,
                    marginLeft: 0,
                    marginRight: 3,
                }}
            >
                <CashPicker
                    onSubmit={onSubmitCashRegister}
                    setSelectedCashRegister={setSelectedCashRegister}
                />
                <Grid
                    container
                    spacing={2}
                    justifyContent={"space-between"}
                    alignItems={"end"}
                    sx={{ marginTop: 2, marginBottom: 2 }}
                >
                    <Grid
                        item
                        align="center"
                        display={"flex"}
                        gap={4}
                        justifyContent="start"
                    >
                        <Button
                            variant="outlined"
                            onClick={() => generateReport(REPORT_TYPES.Z)}
                            disabled={!reportBthVisibility}
                        >
                            {t("z_report")}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => generateReport(REPORT_TYPES.X)}
                            disabled={!reportBthVisibility}
                        >
                            {t("x_report")}
                        </Button>
                    </Grid>
                    <Grid item align="end" display={"flex"} gap={4}>
                        {canNavigateToReports ? (
                            <Button
                                variant="outlined"
                                onClick={() =>
                                    handleReportsNavigate("gestionar")
                                }
                            >
                                {t("gestionar_info")}
                            </Button>
                        ) : null}
                        {canNavigateToReports ? (
                            <Button
                                variant="outlined"
                                onClick={() =>
                                    handleReportsNavigate("periodic")
                                }
                            >
                                {t("periodic_info")}
                            </Button>
                        ) : null}
                    </Grid>
                </Grid>

                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={3} align="center">
                        <DatePickerRange
                            setSortingDates={setSortingDates}
                            isCashSelected={isCashRegisterReadyToSubmit}
                        />
                    </Grid>
                    <Grid item xs={5} sx={{ marginBottom: 1 }} align={"left"}>
                        {isCashRegisterReadyToSubmit && (
                            <Box>
                                <ButtonGroup>
                                    {Object.keys(REPORT_TYPES).map(
                                        (type, index) => (
                                            <Button
                                                variant={
                                                    selectedReportType ===
                                                    `${REPORT_TYPES[type]}`
                                                        ? "contained"
                                                        : "outlined"
                                                }
                                                onClick={() =>
                                                    handleChangeReportType(
                                                        {
                                                            target: {
                                                                value: `${REPORT_TYPES[type]}`,
                                                            },
                                                        },
                                                        index
                                                    )
                                                }
                                            >
                                                {t("report_type_btn", { type })}
                                            </Button>
                                        )
                                    )}
                                </ButtonGroup>
                            </Box>
                        )}
                    </Grid>
                    <Grid item xs={2} align="center">
                        {isCashRegisterReadyToSubmit && (
                            <Autocomplete
                                options={getFromReportOptions()}
                                getOptionLabel={getReportOptionLabel}
                                value={fromReport}
                                onChange={handleFromReportChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t("from")}
                                        variant="standard"
                                        fullWidth
                                        helperText={t("select_first_report")}
                                    />
                                )}
                                isOptionEqualToValue={(option, value) =>
                                    option._id === value._id
                                }
                                disabled={reports.length === 0}
                            />
                        )}
                    </Grid>
                    <Grid item xs={2} align="center">
                        {isCashRegisterReadyToSubmit && (
                            <Autocomplete
                                options={getToReportOptions()}
                                getOptionLabel={getReportOptionLabel}
                                value={toReport}
                                onChange={handleToReportChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t("to")}
                                        variant="standard"
                                        fullWidth
                                        helperText={t("select_last_report")}
                                    />
                                )}
                                isOptionEqualToValue={(option, value) =>
                                    option._id === value._id
                                }
                                disabled={reports.length === 0}
                            />
                        )}
                    </Grid>
                </Grid>

                <Grid container spacing={2} align="left">
                    <Grid item xs={12}>
                        <TableContainer component={Paper}>
                            <Table
                                sx={{ minWidth: 650 }}
                                aria-label="simple table"
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t("id")}</TableCell>
                                        <TableCell align="right">
                                            {t("report")} #
                                        </TableCell>
                                        <TableCell align="right">
                                            {t("date_time")}
                                        </TableCell>
                                        <TableCell align="right">
                                            {t("total_mdl")}
                                        </TableCell>
                                        <TableCell align="right">
                                            {t("total_year_mdl")}
                                        </TableCell>
                                        <TableCell align="right">
                                            {t("type")}
                                        </TableCell>
                                        <TableCell align="right">
                                            {t("cashier_name")}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reports.map((report) => {
                                        return (
                                            <TableRow
                                                hover
                                                key={report._id}
                                                sx={{
                                                    "td, th": {
                                                        cursor: "pointer",
                                                    },
                                                    "&:last-child td, &:last-child th":
                                                        { border: 0 },
                                                }}
                                                onClick={() => {
                                                    const rid =
                                                        selectedCashRegister?._id;
                                                    const qs = rid
                                                        ? `rid=${rid}&${getQueryParams(
                                                              card
                                                          )}`
                                                        : getQueryParams(card);
                                                    navigate(
                                                        `/acps/report/${report._id}?${qs}`
                                                    );
                                                }}
                                            >
                                                <TableCell align="left">
                                                    {report._id}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {report.soap_data.report}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {report.soap_data.datetime}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {
                                                        report.soap_data.daily
                                                            .total
                                                    }
                                                </TableCell>
                                                <TableCell align="right">
                                                    {
                                                        report.soap_data.year
                                                            .total
                                                    }
                                                </TableCell>
                                                <TableCell align="right">
                                                    {report.soap_data.type}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {report.user &&
                                                    report.user.login
                                                        ? report.user.login
                                                        : " "}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>
                    {pageCount > 1 && (
                        <Grid item xs={11} align="center">
                            <Pagination
                                sx={{
                                    my: 5,
                                    justifyContent: "center",
                                    display: "flex",
                                }}
                                count={pageCount}
                                variant="outlined"
                                shape="rounded"
                                page={+currentPage}
                                onChange={handlePagination}
                            />
                        </Grid>
                    )}
                </Grid>
                <Snackbar
                    open={openSuccess}
                    autoHideDuration={6000}
                    onClose={handleClose}
                >
                    <Alert
                        onClose={handleClose}
                        severity="success"
                        sx={{ width: "100%" }}
                    >
                        {t("raport_creat")}
                    </Alert>
                </Snackbar>
            </Box>
        </>
    );
}

export default Reports;
